/**
 * The HTTP client. Paths, verbs, query params and response shapes all come
 * from product/api.json — this file is a transcription of that contract, not
 * a design of its own. The token lives in an httpOnly cookie, so every request
 * just carries credentials.
 */
import type {
  Account,
  AnalysisStatus,
  ApiError as ApiErrorBody,
  AuthIdentity,
  AuthMethods,
  AuthSession,
  BankConnection,
  BetaAccessView,
  BetaNotificationStatus,
  CashFlow,
  CategoryId,
  DebtPlan,
  FeeGroup,
  FeeGroupFeeKind,
  HealthScore,
  Money,
  Progress,
  Recommendation,
  RecurringSeries,
  Statement,
  Transaction,
} from '@/generated/types'
import { bridge } from './bridge'
import { isEdgeCode } from './edge'

export type FeeKind = FeeGroupFeeKind

export const API_BASE = '/api/v1'

/** Composite response bodies, exactly as declared in product/api.json. */
export type AuthVerifyResponse = AuthSession
export interface StatementPreview {
  statement: Statement
  transactions: Transaction[]
}
export interface ConfirmImportResponse {
  statement: Statement
  account: Account
  importedCount: number
  skippedCount: number
}
export interface Page<T> {
  items: T[]
  nextCursor?: string | null
}
export interface DebtSummary {
  accounts: Account[]
  totalOwed: Money
  monthlyInterest: Money
  utilization: number | null
  averageAprBasisPoints: number | null
}
export interface NotificationSettings {
  newAnalysis: boolean
  priceIncrease: boolean
  fee: boolean
}
export interface UserSettings {
  email: string
  currency: string
  emergencyFundTarget: Money | null
  notifications: NotificationSettings
}
export interface ExportResponse {
  url: string
  expiresAt: string
}
/**
 * Short-lived token that opens the provider's hosted Link UI. This and the
 * public token Link hands back are the only provider credentials a client ever
 * holds — the durable access token stays encrypted on the Server.
 */
export interface LinkTokenResponse {
  linkToken: string
  expiration: string
}
export interface ExchangeResponse {
  connection: BankConnection
  accounts: Account[]
}

export class ApiFailure extends Error {
  readonly body: ApiErrorBody
  readonly status: number
  constructor(body: ApiErrorBody, status: number) {
    super(body.message ?? body.code)
    this.name = 'ApiFailure'
    this.body = body
    this.status = status
  }
  get copyKey(): string {
    return this.body.copyKey
  }
  get retryable(): boolean {
    return this.body.retryable ?? true
  }
}

export function asApiFailure(e: unknown): ApiFailure {
  if (e instanceof ApiFailure) return e
  return new ApiFailure(
    { code: 'serverError', copyKey: 'error.generic', retryable: true, message: String(e) },
    0,
  )
}

/** Swappable so the dev mock backend can stand in for the Server surface. */
export type Transport = (request: Request) => Promise<Response>
let transport: Transport = (request) => fetch(request)
export function setTransport(next: Transport): void {
  transport = next
}

let onUnauthorized: (() => void) | null = null
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler
}

/**
 * Reachability of the origin, reported from the one place every request already
 * passes through.
 *
 * The four edge codes are not a per-screen problem and are not a session
 * problem — they say the mini did not answer at all — so they are raised here
 * once rather than handled at thirty call sites. `onOriginReached` is the other
 * half: any successful response proves the origin is back, which is what
 * retires the serviceUnavailable interstitial without the client having to
 * guess whether a retry worked.
 */
let onEdgeFailure: ((failure: ApiFailure) => void) | null = null
export function setEdgeFailureHandler(handler: ((failure: ApiFailure) => void) | null): void {
  onEdgeFailure = handler
}

let onOriginReached: (() => void) | null = null
export function setOriginReachedHandler(handler: (() => void) | null): void {
  onOriginReached = handler
}

interface RequestOptions {
  query?: Record<string, string | number | boolean | undefined | null>
  body?: unknown
  form?: FormData
  idempotencyKey?: string
  signal?: AbortSignal
  /**
   * A restricted beta capability is not an app session, so nothing it returns
   * may sign the user out. An expired offer is 410, not 401, so this guard is
   * not normally reached — it stays because the capability endpoints are the
   * one place a 401 would mean "this offer", never "this session".
   */
  handleUnauthorized?: boolean
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(API_BASE + path, location.origin)
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue
    url.searchParams.set(key, String(value))
  }
  return url.pathname + url.search
}

const isApiErrorBody = (v: unknown): v is ApiErrorBody =>
  typeof v === 'object' && v !== null && typeof (v as ApiErrorBody).copyKey === 'string'

async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers()
  let body: BodyInit | undefined
  if (options.form) {
    body = options.form
  } else if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json')
    body = JSON.stringify(options.body)
  }
  if (options.idempotencyKey) headers.set('Idempotency-Key', options.idempotencyKey)
  headers.set('Accept', 'application/json')

  const req = new Request(buildUrl(path, options.query), {
    method,
    headers,
    body,
    credentials: 'include',
    signal: options.signal,
  })

  let response: Response
  try {
    response = await transport(req)
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') throw e
    bridge.log(`api: ${method} ${path} could not reach the server`)
    throw new ApiFailure(
      { code: 'serverError', copyKey: 'error.network', retryable: true, message: String(e) },
      0,
    )
  }

  // Any answer at all — including a 4xx — proves the request reached the mini,
  // because only the origin can produce one. That is the signal the origin
  // interstitial retires itself on. The edge codes below are the exception and
  // are handled after the body is read.
  if (response.ok) onOriginReached?.()

  if (response.status === 204) return undefined as T

  const text = await response.text()
  let payload: unknown = undefined
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = undefined
    }
  }

  if (!response.ok) {
    if (response.status === 401 && options.handleUnauthorized !== false) {
      onUnauthorized?.()
      throw new ApiFailure(
        { code: 'unauthorized', copyKey: 'error.session', retryable: false },
        401,
      )
    }
    const errorBody: ApiErrorBody = isApiErrorBody(payload)
      ? payload
      : {
          code: response.status >= 500 ? 'serverError' : 'validationFailed',
          copyKey: response.status >= 500 ? 'error.server' : 'error.generic',
          retryable: response.status >= 500,
          message: text.slice(0, 200),
        }
    bridge.log(`api: ${method} ${path} -> ${response.status} ${errorBody.code}`)
    const failure = new ApiFailure(errorBody, response.status)
    // Raised globally, never per screen: the edge codes mean nothing reached
    // the origin, so the screen's own error copy would blame the wrong thing.
    if (isEdgeCode(errorBody.code)) onEdgeFailure?.(failure)
    throw failure
  }

  return payload as T
}

/**
 * Is the Server answering at all? Deliberately NOT routed through request():
 * an unauthenticated 401 still proves reachability, and firing the global
 * unauthorized handler here would bounce welcome straight past itself.
 */
export async function probeServer(): Promise<boolean> {
  try {
    await transport(
      new Request(buildUrl('/settings'), {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      }),
    )
    return true
  } catch {
    return false
  }
}

export interface TransactionQuery {
  accountId?: string
  category?: CategoryId
  from?: string
  to?: string
  q?: string
  recurring?: boolean
  fees?: boolean
  uncategorized?: boolean
  /** Backs the transactions.filter.spending / .income chips. */
  direction?: 'inflow' | 'outflow'
  limit?: number
  cursor?: string | null
}

export const api = {
  auth: {
    /**
     * Which sign-in methods this deployment offers. Public, unauthenticated,
     * and about the deployment rather than any user. Fetched once when signIn
     * mounts and never polled — see auth-methods.schema.json.
     */
    methods: (signal?: AbortSignal) =>
      request<AuthMethods>('GET', '/auth/methods', { query: { surface: 'web' }, signal }),
    sendCode: (email: string) => request<void>('POST', '/auth/code', { body: { email } }),
    verify: (email: string, code: string) =>
      request<AuthVerifyResponse>('POST', '/auth/verify', { body: { email, code } }),
    /**
     * Static credentials, self-hosted deploys only. Issues the identical
     * session as /auth/verify, and 404s where the Server did not mount it.
     * The password reaches the network and nothing else: it is never logged,
     * stored or put in a query string.
     */
    password: (username: string, password: string) =>
      request<AuthVerifyResponse>('POST', '/auth/password', { body: { username, password } }),
    reauthenticatePassword: (username: string, password: string) =>
      request<void>('POST', '/auth/reauth/password', { body: { username, password } }),
    identities: (signal?: AbortSignal) => request<AuthIdentity[]>('GET', '/auth/identities', { signal }),
    removeIdentity: (id: string) => request<void>('DELETE', `/auth/identities/${id}`),
    googleWebStartUrl: (intent: 'signIn' | 'link' | 'reauthenticate', returnTo: string) =>
      buildUrl('/auth/social/google/web/start', { intent, returnTo }),
    signOut: () => request<void>('POST', '/auth/signout'),
  },

  betaNotifications: {
    get: (signal?: AbortSignal) =>
      request<BetaAccessView>('GET', '/beta-notifications', { signal, handleUnauthorized: false }),
    join: () =>
      request<BetaNotificationStatus>('POST', '/beta-notifications', {
        body: { consent: true },
        handleUnauthorized: false,
      }),
    decline: () =>
      request<void>('DELETE', '/beta-notifications', { handleUnauthorized: false }),
  },

  statements: {
    upload: (file: File, options: { accountId?: string; password?: string; idempotencyKey: string }) => {
      const form = new FormData()
      form.append('file', file)
      if (options.accountId) form.append('accountId', options.accountId)
      if (options.password) form.append('password', options.password)
      return request<Statement>('POST', '/statements', {
        form,
        idempotencyKey: options.idempotencyKey,
      })
    },
    get: (id: string, signal?: AbortSignal) => request<Statement>('GET', `/statements/${id}`, { signal }),
    preview: (id: string, signal?: AbortSignal) =>
      request<StatementPreview>('GET', `/statements/${id}/preview`, { signal }),
    unlock: (id: string, password: string) =>
      request<Statement>('POST', `/statements/${id}/unlock`, { body: { password } }),
    confirm: (
      id: string,
      body: { accountId: string | null; newAccount: { name: string; type: Account['type'] } | null },
      idempotencyKey: string,
    ) => request<ConfirmImportResponse>('POST', `/statements/${id}/confirm`, { body, idempotencyKey }),
    remove: (id: string) => request<void>('DELETE', `/statements/${id}`),
  },

  accounts: {
    list: (signal?: AbortSignal) => request<Account[]>('GET', '/accounts', { signal }),
    get: (id: string, signal?: AbortSignal) => request<Account>('GET', `/accounts/${id}`, { signal }),
    update: (
      id: string,
      body: Partial<Pick<Account, 'name' | 'type' | 'aprBasisPoints' | 'creditLimit' | 'minimumPayment'>>,
    ) => request<Account>('PATCH', `/accounts/${id}`, { body }),
    remove: (id: string) => request<void>('DELETE', `/accounts/${id}`),
    statements: (id: string) => request<Statement[]>('GET', `/accounts/${id}/statements`),
  },

  transactions: {
    list: (query: TransactionQuery = {}, signal?: AbortSignal) =>
      request<Page<Transaction>>('GET', '/transactions', {
        query: { ...query, cursor: query.cursor ?? undefined },
        signal,
      }),
    get: (id: string, signal?: AbortSignal) => request<Transaction>('GET', `/transactions/${id}`, { signal }),
    update: (
      id: string,
      body: {
        category?: CategoryId
        isRecurring?: boolean
        isFee?: boolean
        feeKind?: FeeKind
        notes?: string
        applyToMerchant?: boolean
      },
    ) => request<Transaction>('PATCH', `/transactions/${id}`, { body }),
  },

  analysis: {
    score: () => request<HealthScore>('GET', '/analysis/score'),
    cashFlow: (months = 12) => request<CashFlow>('GET', '/analysis/cash-flow', { query: { months } }),
    recurring: (status?: string, signal?: AbortSignal) =>
      request<RecurringSeries[]>('GET', '/analysis/recurring', { query: { status }, signal }),
    recurringSeries: (id: string) => request<RecurringSeries>('GET', `/analysis/recurring/${id}`),
    updateRecurring: (id: string, body: { status?: 'cancelledByUser'; userConfirmed?: boolean }) =>
      request<RecurringSeries>('PATCH', `/analysis/recurring/${id}`, { body }),
    fees: (months = 12) => request<FeeGroup[]>('GET', '/analysis/fees', { query: { months } }),
    debt: () => request<DebtSummary>('GET', '/analysis/debt'),
    plan: (monthlyPayment: Money, strategy: DebtPlan['strategy']) =>
      request<DebtPlan>('POST', '/analysis/debt/plan', { body: { monthlyPayment, strategy } }),
    savePlan: (plan: DebtPlan) => request<DebtPlan>('PUT', '/analysis/debt/plan', { body: plan }),
    refresh: () => request<void>('POST', '/analysis/refresh'),
    /**
     * Two rows, no model call — the only endpoint safe to poll, and the only
     * one clients poll at all. Backs analysisPending and analysisFailed.
     */
    status: () => request<AnalysisStatus>('GET', '/analysis/status'),
    /**
     * The only user-triggered model call. 202 and then poll; the prose arrives
     * on the next status change, not in this response.
     */
    explain: (body: { target: 'score' | 'recommendation'; id?: string }) =>
      request<void>('POST', '/analysis/explain', { body }),
  },

  recommendations: {
    list: (query: { theme?: string; status?: string } = {}) =>
      request<Recommendation[]>('GET', '/recommendations', { query }),
    get: (id: string, signal?: AbortSignal) =>
      request<Recommendation>('GET', `/recommendations/${id}`, { signal }),
    setStatus: (id: string, status: Recommendation['status']) =>
      request<Recommendation>('PATCH', `/recommendations/${id}`, { body: { status } }),
  },

  /**
   * Bank linking — the second ingestion path into the same pipeline. Nothing
   * here returns a provider access token; `POST /plaid/webhook` is
   * provider-to-Server only and deliberately has no client binding.
   */
  plaid: {
    linkToken: (body: { accountId?: string | null; relinkItemId?: string | null } = {}) =>
      request<LinkTokenResponse>('POST', '/plaid/link-token', {
        body: { accountId: body.accountId ?? null, relinkItemId: body.relinkItemId ?? null },
      }),
    exchange: (publicToken: string) =>
      request<ExchangeResponse>('POST', '/plaid/exchange', { body: { publicToken } }),
    items: (signal?: AbortSignal) => request<BankConnection[]>('GET', '/plaid/items', { signal }),
    item: (id: string, signal?: AbortSignal) =>
      request<BankConnection>('GET', `/plaid/items/${id}`, { signal }),
    sync: (id: string) => request<void>('POST', `/plaid/items/${id}/sync`),
    relink: (id: string) => request<LinkTokenResponse>('POST', `/plaid/items/${id}/relink`),
    remove: (id: string) => request<void>('DELETE', `/plaid/items/${id}`),
  },

  progress: (range: Progress['range'] = '12m') => request<Progress>('GET', '/progress', { query: { range } }),

  settings: {
    get: () => request<UserSettings>('GET', '/settings'),
    update: (body: Partial<Omit<UserSettings, 'email'>> & { email?: string }) =>
      request<UserSettings>('PATCH', '/settings', { body }),
  },

  exportData: (body: {
    format: 'csv' | 'json'
    scope: 'transactions' | 'analysis' | 'everything'
    from?: string
    to?: string
  }) => request<ExportResponse>('POST', '/export', { body }),

  me: {
    deleteStatements: () => request<void>('DELETE', '/me/statements'),
    deleteEverything: () => request<void>('DELETE', '/me'),
  },
}
